<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>举报管理</h1>
        <p>处理用户对动态、评论、回复的举报。判定成立的，内容立刻对其他人隐藏，仅作者本人可见</p>
      </div>
      <div class="head-actions">
        <el-button class="ghost-btn" icon="el-icon-refresh" @click="fetchList">刷新</el-button>
      </div>
    </div>

    <!-- ── 指标 ───────────────────────────────────────────── -->
    <div class="stat-row">
      <div
        class="stat-card is-clickable is-warn"
        :class="{ 'is-active': query.status === 'PENDING' }"
        @click="quickFilter('PENDING')"
      >
        <div class="stat-num">{{ pendingCount || 0 }}</div>
        <div class="stat-label">待处理举报</div>
        <div class="stat-sub">按举报时间从早到晚</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.status === 'ALL' }"
        @click="quickFilter('ALL')"
      >
        <div class="stat-num">{{ total }}</div>
        <div class="stat-label">当前筛选结果</div>
        <div class="stat-sub">含已成立 / 已驳回</div>
      </div>
    </div>

    <!-- ── 说明 ───────────────────────────────────────────── -->
    <div class="hint-bar report-hint">
      <i class="el-icon-info" />
      <span>
        <b>判定成立 = 内容转「违规」。</b>动态会从心动圈消失、只在作者的「我发布的动态」里带违规标签；评论与回复的正文会被换成「此内容已违规」占位，<b>楼层与其下的回复不受影响</b>。
        处罚是另一件事 —— 默认不处罚，需要时可以单独给作者禁言或禁止发布动态，期满自动恢复。
      </span>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="query.keyword"
        placeholder="搜索被举报内容"
        clearable
        prefix-icon="el-icon-search"
        class="search-input report-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-select v-model="query.targetType" class="type-select" @change="handleSearch">
        <el-option label="全部类型" value="" />
        <el-option label="动态" value="MOMENT" />
        <el-option label="评论" value="COMMENT" />
        <el-option label="回复" value="REPLY" />
      </el-select>
      <el-select v-model="query.status" class="status-select" @change="handleSearch">
        <el-option label="待处理" value="PENDING" />
        <el-option label="举报成立" value="ACCEPTED" />
        <el-option label="已驳回" value="REJECTED" />
        <el-option label="全部" value="ALL" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleSearch">查询</el-button>
      <el-button class="ghost-btn" @click="resetQuery">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="loading" :data="list" style="width: 100%">
        <el-table-column label="被举报内容" min-width="300">
          <template slot-scope="{ row }">
            <div class="target-cell">
              <span class="type-chip">{{ row.targetLabel }}</span>
              <span v-if="!row.targetExists" class="gone-chip">内容已删</span>
            </div>
            <div class="cell-main content-clamp">{{ row.contentSnapshot || '（无文字内容）' }}</div>
            <div class="thumb-row" v-if="row.images && row.images.length">
              <img v-for="(img, i) in row.images.slice(0, 4)" :key="i" class="thumb" :src="img" />
              <span v-if="row.images.length > 4" class="thumb-more">+{{ row.images.length - 4 }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="举报理由" width="150">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.reasonLabel }}</div>
            <div v-if="row.description" class="cell-sub content-clamp">{{ row.description }}</div>
          </template>
        </el-table-column>

        <el-table-column label="举报人" width="150">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <img class="owner-avatar" :src="row.reporterAvatar || defaultAvatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.reporterNickname || '未设置昵称' }}</div>
                <div class="owner-school">{{ row.createdAt || '—' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="被举报人" width="170">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <img class="owner-avatar" :src="row.reportedAvatar || defaultAvatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.reportedNickname || '未设置昵称' }}</div>
                <div class="owner-school">{{ row.schoolName || '—' }}</div>
              </div>
            </div>
            <div v-if="row.reportedUserAcceptedCount > 0" class="repeat-chip">
              曾被判定 {{ row.reportedUserAcceptedCount }} 次
            </div>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="pillClass(row.status)">
              <i />{{ row.statusLabel }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-plain" @click="openDetail(row)">查看</button>
              <template v-if="row.status === 'PENDING'">
                <button type="button" class="act act-danger" @click="openDetail(row, 'ACCEPT')">判定违规</button>
                <button type="button" class="act" @click="reject(row)">驳回</button>
              </template>
              <span v-else class="row-done">{{ row.handledByName || '已处理' }}</span>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-circle-check" />
            <p>没有待处理的举报</p>
          </div>
        </template>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="query.page"
          :page-size.sync="query.size"
          :page-sizes="[10, 20, 50]"
          @current-change="fetchList"
          @size-change="handleSearch"
        />
      </div>
    </div>

    <!-- ── 详情 / 处置（一屏纵览） ─────────────────────────── -->
    <el-drawer
      :visible.sync="drawerVisible"
      size="620px"
      custom-class="admin-drawer"
      :with-header="false"
    >
      <div class="report-detail">
        <div class="report-detail__head">
          <div>
            <div class="report-detail__title">
              {{ current.targetLabel }}举报
              <span class="status-pill" :class="pillClass(current.status)">
                <i />{{ current.statusLabel }}
              </span>
            </div>
            <div class="report-detail__meta">
              {{ current.reasonLabel }} · {{ current.createdAt || '—' }}
            </div>
          </div>
          <button type="button" class="act act-plain" @click="drawerVisible = false">关闭</button>
        </div>

        <!-- 被举报内容 -->
        <div class="report-detail__section">
          <div class="report-detail__label">
            被举报内容
            <span v-if="!current.targetExists" class="gone-chip">内容已被删除，仅凭快照判断</span>
          </div>
          <div class="report-detail__content">{{ current.contentSnapshot || '（无文字内容）' }}</div>
          <div v-if="current.images && current.images.length" class="report-gallery">
            <el-image
              v-for="(img, i) in current.images"
              :key="i"
              class="report-gallery__img"
              :src="img"
              :preview-src-list="current.images"
              :initial-index="i"
              fit="cover"
            />
          </div>
          <div v-if="current.description" class="report-detail__desc">
            举报人补充：{{ current.description }}
          </div>
        </div>

        <!-- 双方 -->
        <div class="report-detail__section">
          <div class="report-detail__label">涉及用户</div>
          <div class="party-row">
            <div class="party">
              <img class="party__avatar" :src="current.reporterAvatar || defaultAvatar" />
              <div>
                <div class="party__name">{{ current.reporterNickname || '未设置昵称' }}</div>
                <div class="party__role">举报人</div>
              </div>
            </div>
            <div class="party">
              <img class="party__avatar" :src="current.reportedAvatar || defaultAvatar" />
              <div>
                <div class="party__name">{{ current.reportedNickname || '未设置昵称' }}</div>
                <div class="party__role">
                  被举报人 · {{ current.schoolName || '—' }}
                  <span v-if="current.reportedUserAcceptedCount > 0" class="repeat-chip">
                    历史 {{ current.reportedUserAcceptedCount }} 次成立
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 处置表单 -->
        <template v-if="current.status === 'PENDING'">
          <div class="report-detail__section">
            <div class="report-detail__label">处理说明（会发给作者，必填）</div>
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="2"
              maxlength="100"
              show-word-limit
              placeholder="例如：图片含有明显性暗示，请修改后重新发布"
            />
          </div>

          <div class="report-detail__section">
            <div class="report-detail__label">对作者的处理（可选，不影响内容判定）</div>
            <div class="punish-row">
              <el-select v-model="form.punishType" class="punish-type">
                <el-option label="不处罚" value="NONE" />
                <el-option label="禁言（不能评论/回复）" value="MUTE" />
                <el-option label="禁止发布动态" value="MOMENT_BAN" />
                <el-option label="禁言 + 禁止发布动态" value="ALL" />
              </el-select>
              <el-select
                v-model="form.punishHours"
                class="punish-hours"
                :disabled="form.punishType === 'NONE'"
              >
                <el-option v-for="opt in hourOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </div>
            <div v-if="form.punishType !== 'NONE'" class="punish-hint">
              到期后自动恢复，无需人工解封。当前选择：
              <b>{{ punishText }}</b>
            </div>
          </div>
        </template>

        <!-- 已处理结果 -->
        <div v-else class="report-detail__section">
          <div class="report-detail__label">处理结果</div>
          <div class="report-detail__reason">
            <div>处理人：{{ current.handledByName || '—' }} · {{ current.handledAt || '—' }}</div>
            <div v-if="current.handleRemark">说明：{{ current.handleRemark }}</div>
          </div>
        </div>

        <div class="report-detail__footer">
          <el-button class="ghost-btn" @click="drawerVisible = false">关闭</el-button>
          <template v-if="current.status === 'PENDING'">
            <el-button class="ghost-btn" @click="reject(current)">驳回举报</el-button>
            <el-button type="danger" class="danger-btn" @click="accept(current)">判定违规</el-button>
          </template>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getReportList, getReportDetail, acceptReport, rejectReport } from '@/api/report'

export default {
  name: 'ReportManage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      pendingCount: 0,
      query: {
        page: 1,
        size: 10,
        status: 'PENDING',
        targetType: '',
        keyword: ''
      },
      drawerVisible: false,
      current: {},
      form: {
        remark: '',
        punishType: 'NONE',
        punishHours: 24
      },
      defaultAvatar: 'https://xdmh.xbjava.com/static/default-avatar.png'
    }
  },
  computed: {
    hourOptions() {
      return [
        { label: '1 天', value: 24 },
        { label: '3 天', value: 72 },
        { label: '7 天', value: 168 },
        { label: '30 天', value: 720 },
        { label: '永久', value: 0 }
      ]
    },
    punishText() {
      const scope = this.punishScopeText(this.form.punishType)
      const hour = this.hourOptions.find(o => o.value === this.form.punishHours)
      return scope + ' · ' + (hour ? hour.label : '1 天')
    }
  },
  created() {
    // 深链：钉钉消息带 ?focus=<举报ID>，直接开这条举报的处理抽屉。
    // 先在当前页里找，找不到就单独拉详情 —— 举报是"最早优先"排序，
    // 刚进来的那条一定在最后一页，只靠翻页找会很烦。
    this.fetchList().then(() => this.applyFocus())
  },
  methods: {
    fetchList() {
      this.loading = true
      getReportList({
        page: this.query.page,
        size: this.query.size,
        status: this.query.status,
        targetType: this.query.targetType || undefined,
        keyword: this.query.keyword || undefined
      })
        .then(res => {
          const data = res.data || {}
          this.list = data.list || []
          this.total = data.total || 0
          this.pendingCount = data.pendingCount || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.query.page = 1
      this.fetchList()
    },
    applyFocus() {
      const id = Number(this.$route.query.focus)
      if (!id) return
      const inList = this.list.find(item => item.id === id)
      if (inList) {
        this.openDetail(inList)
      } else {
        getReportDetail(id)
          .then(res => {
            if (res && res.data) this.openDetail(res.data)
          })
          .catch(() => {})
      }
      const query = { ...this.$route.query }
      delete query.focus
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    resetQuery() {
      this.query = { page: 1, size: 10, status: 'PENDING', targetType: '', keyword: '' }
      this.fetchList()
    },
    quickFilter(status) {
      this.query.status = status
      this.handleSearch()
    },
    openDetail(row, intent) {
      this.current = row
      this.form = { remark: '', punishType: 'NONE', punishHours: 24 }
      this.drawerVisible = true
      // 从列表直接点「判定违规」时，把焦点留给「处理说明」——
      // 处置必须写理由，让人先看到输入框比先看到一堆说明更有效
      if (intent === 'ACCEPT') {
        this.$nextTick(() => {
          const el = this.$el.querySelector('.report-detail textarea')
          if (el) el.focus()
        })
      }
    },
    accept(row) {
      if (!row || !row.id) return
      if (!this.form.remark || !this.form.remark.trim()) {
        this.$message.warning('请填写处理说明，这段话会发给作者')
        return
      }
      const punish = this.form.punishType !== 'NONE'
      const tip = punish
        ? `内容将转为违规，并对作者执行「${this.punishText}」。确认？`
        : '内容将转为违规，从心动圈消失、仅作者本人可见。确认？'
      this.$confirm(tip, '判定违规', {
        confirmButtonText: '确认判定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          return acceptReport(row.id, {
            remark: this.form.remark.trim(),
            punishType: this.form.punishType,
            punishHours: punish ? this.form.punishHours : null
          })
        })
        .then(res => {
          this.$message.success(res.message || '已判定违规')
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    reject(row) {
      if (!row || !row.id) return
      this.$prompt('驳回说明可以不填，仅用于内部留痕', '驳回举报', {
        confirmButtonText: '确认驳回',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：内容正常，属误报',
        inputValidator: value => {
          if (value && value.length > 100) return '说明请控制在 100 字以内'
          return true
        }
      })
        .then(({ value }) => {
          return rejectReport(row.id, { remark: value ? value.trim() : null })
        })
        .then(res => {
          this.$message.success(res.message || '已驳回')
          this.drawerVisible = false
          this.fetchList()
        })
        .catch(() => {})
    },
    punishScopeText(type) {
      if (type === 'MUTE') return '禁言'
      if (type === 'MOMENT_BAN') return '禁止发布动态'
      if (type === 'ALL') return '禁言 + 禁止发布动态'
      return '不处罚'
    },
    pillClass(status) {
      if (status === 'ACCEPTED') return 's2'
      if (status === 'REJECTED') return 's1'
      return 's0'
    }
  }
}
</script>

<style lang="scss" scoped>
.stat-card.is-clickable {
  cursor: pointer;
  user-select: none;
}

.stat-card.is-clickable:hover {
  border-color: var(--ink-300);
}

.stat-card.is-clickable.is-active {
  border-color: var(--rose-400);
  box-shadow: 0 0 0 3px var(--rose-50);
}

.report-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  line-height: 1.7;
}

.report-hint i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.report-search {
  width: 240px;
}

.type-select,
.status-select {
  width: 130px;
}

.content-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.target-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.type-chip {
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--ink-100);
  color: var(--ink-500);
  line-height: 1.6;
}

.gone-chip {
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--warn-bg);
  color: var(--warn-text);
  line-height: 1.6;
  white-space: nowrap;
}

.repeat-chip {
  display: inline-block;
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--rose-50);
  color: var(--rose-600);
  line-height: 1.6;
  margin-left: 4px;
  white-space: nowrap;
}

.row-done {
  font-size: 12.5px;
  color: var(--ink-400);
}

.danger-btn {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.danger-btn:hover,
.danger-btn:focus {
  background: var(--rose-500);
  border-color: var(--rose-500);
  color: #fff;
}

/* 列表缩略图 */
.thumb-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.thumb {
  width: 38px;
  height: 38px;
  border-radius: var(--r-sm);
  object-fit: cover;
  background: var(--ink-100);
}

.thumb-more {
  font-size: 12px;
  color: var(--ink-400);
}

/* 抽屉 */
.report-detail {
  padding: 24px 28px 28px;
}

.report-detail__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--ink-100);
}

.report-detail__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ink-900);
  display: flex;
  align-items: center;
  gap: 10px;
}

.report-detail__meta {
  font-size: 12.5px;
  color: var(--ink-400);
  margin-top: 4px;
}

.report-detail__section {
  margin-top: 22px;
}

.report-detail__label {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-500);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.report-detail__content {
  font-size: 14px;
  line-height: 1.8;
  color: var(--ink-900);
  white-space: pre-wrap;
  word-break: break-word;
  background: var(--ink-100);
  border-radius: var(--r-md);
  padding: 12px 14px;
}

.report-detail__desc {
  font-size: 13px;
  color: var(--ink-500);
  line-height: 1.7;
  margin-top: 10px;
}

.report-detail__reason {
  font-size: 13px;
  line-height: 1.9;
  color: var(--ink-500);
}

.report-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
}

.report-gallery__img {
  width: 100%;
  height: 120px;
  border-radius: var(--r-md);
  background: var(--ink-100);
  display: block;
}

.party-row {
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
}

.party {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.party__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
  background: var(--ink-100);
}

.party__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-900);
}

.party__role {
  font-size: 12px;
  color: var(--ink-400);
  margin-top: 2px;
}

.punish-row {
  display: flex;
  gap: 10px;
}

.punish-type {
  flex: 1;
}

.punish-hours {
  width: 120px;
}

.punish-hint {
  font-size: 12.5px;
  color: var(--ink-400);
  margin-top: 8px;
  line-height: 1.7;
}

.punish-hint b {
  color: var(--rose-600);
  font-weight: 600;
}

.report-detail__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--ink-100);
}
</style>
